import { Injectable, inject } from '@angular/core';
import {Outlook} from './api/fit-api/models/indicators/outlook-info';
import {countBy} from './functions/count-by';
import {OutlookIndicator} from '../models/outlook-indicator';
import {DatasetDetail, DatasetType} from './api/fit-api/models/datasets/dataset-type';
import {GovernmentId} from './api/fit-api/models/government-id';
import {forkJoin, Observable} from 'rxjs';
import {IndicatorReport} from './api/fit-api/models/indicators/indicator-report';
import {map} from 'rxjs/operators';
import {FitApiService} from './api/fit-api/fit-api.service';
import {IndicatorGrouping} from '../models/indicator-grouping';
import {TrendAmount} from '../models/trend-amount';
import {Indicator} from '../models/indicator';
import {GovernmentSpecificity} from './api/fit-api/models/government-specificity';
import {OUTLOOKS} from '../models/outlook.config';
import {FilingStatusService} from "./filing-status.service";
import {sortByProperty} from "./functions/sort-by-property";

@Injectable({
	providedIn: 'root'
})
export default class FinancialHealthService {
	private apiService = inject(FitApiService);
	private filingStatusService = inject(FilingStatusService);

	public static supportedDatasetTypes = [
		DatasetType.Snapshot, // Default dataset type should be first in the array
	] as const;

	readonly defaultOutlookIndicator = new OutlookIndicator('Indeterminate', 'Not enough information available to make a determination');

	// todo - remove these temporary sample values used on style guide and prototyping for each explore option
	outlookGood = new OutlookIndicator('Good');
	outlookCautionary = new OutlookIndicator('Cautionary');
	outlookConcerning = new OutlookIndicator('Concerning');
	outlookIndeterminate = new OutlookIndicator('Indeterminate');
	// Example of a group of FHI outlooks
	outlooks: Array<OutlookIndicator> = [
		this.outlookCautionary,
		this.outlookGood,
		this.outlookGood,
		this.outlookConcerning,
		this.outlookIndeterminate,
		this.outlookGood,
		this.outlookCautionary,
		this.outlookCautionary,
		this.outlookCautionary,
		this.outlookConcerning,
	];

	public getOutlookCounts = (outlooks?: Array<OutlookIndicator | undefined>) => {
		if (outlooks) {
			const counts = countBy(outlooks, o => o?.outlook);
			return Object.entries(counts).map(([outlook, count]) => new OutlookIndicator(outlook as Outlook, null, count as number))
				.sort((a, b) => {
					const aConfig = OUTLOOKS.find(o => o.outlook === a.outlook);
					const bConfig = OUTLOOKS.find(o => o.outlook === b.outlook);
					return (aConfig?.sortOrder ?? 100) - (bConfig?.sortOrder ?? 100);
				});
		} else {
			return undefined;
		}
	}

	/**
	 * Return view model (Indicator Reports) for gov and gov type profile financial health section
	 * @param datasetDetail
	 * @param governmentId
	 * @param displayYear
	 * @param numOfTrendYears
	 */
	getIndicatorGroupsForGovernmentId(
		datasetDetail: DatasetDetail,
		governmentId: GovernmentId,
		displayYear: number,
		numOfTrendYears: number
	): Observable<Array<IndicatorGrouping>> {
		const filter = `year gt ${displayYear - numOfTrendYears} and Year le ${displayYear} and ${governmentId.parameterName} eq '${governmentId}'`;
		const indicators = this.apiService.getIndicatorReports(datasetDetail, filter);
		const filingStatuses = this.filingStatusService.getFilingStatusesForYearRange(datasetDetail, displayYear - (numOfTrendYears - 1), displayYear, governmentId);

		return forkJoin([indicators,filingStatuses]).pipe(map(([indicatorReports, filingStatus]) => {
			const indicatorGroups = indicatorReports.reduce((groups: Array<IndicatorGrouping>, indicatorRow: IndicatorReport) => {
				if (governmentId.specificity === GovernmentSpecificity.Government && indicatorRow.measure === null) {
					return groups;
				}

				// For government type profile, we want to group all enterprise fund rows together
				const currentGroupName = governmentId.specificity === GovernmentSpecificity.GovernmentType &&
					indicatorRow.report === 'EnterpriseFundIndicators' ? 'Enterprise Funds' : indicatorRow.group;

				// reduce reports into groups
				let indicatorGroup = groups.find((indicatorGroup: IndicatorGrouping) => indicatorGroup.group === currentGroupName);
				// if group doesn't exist create it
				if (!indicatorGroup) {
					indicatorGroup = new IndicatorGrouping(currentGroupName, governmentId, [])

					groups.push(indicatorGroup)
				}

				// We are creating one indicator per government if this is for the government type profile, so include MCAG in lookup
				let indicator = indicatorGroup.indicators?.find((indicator: Indicator) =>
					indicator.mcag === indicatorRow.mcag && indicator.instanceCode === indicatorRow.instanceCode
				);
				// if the indicator doesn't exist create it
				if (!indicator) {

					// set indicator info meaning to each indicator (lookup by code)
					const indicatorName = datasetDetail.detail.indicatorNames.find(indicatorName =>
						indicatorName.code === indicatorRow.code);

					const infoMeaning = indicatorName?.indicatorInfo.meaning

					indicator = new Indicator(
						indicatorRow.mcag,
						indicatorRow.code,
						indicatorRow.instanceCode,
						indicatorRow.title,
						indicatorRow.measureBenchmark,
						indicatorRow.measureUnitInfo,
						// Add null trend amounts for all expected years
						filingStatus.sort(sortByProperty(fs => fs.year))
							.map(fs => new TrendAmount(fs.year, null)),
						displayYear,
						infoMeaning,
						this.defaultOutlookIndicator,
						indicatorRow.measureSeriesType);
					indicatorGroup.indicators?.push(indicator);
				}

				// Replace the null trend amount with true measure value
				const index = indicator.trendAmounts.findIndex(ta => ta.year === indicatorRow.year);
				if (index !== -1) {
					indicator.trendAmounts[index] = new TrendAmount(indicatorRow.year, indicatorRow.measure);
				}

				// if the current indicator report year equals the display year then set additional indicator properties
				if (indicatorRow.year === displayYear) {
					indicator.outlookIndicatorForDisplayYear = new OutlookIndicator(indicatorRow.outlookInfo.outlook, indicatorRow.outlookInfo.annotation);
					indicator.measureForDisplayYear = indicatorRow.measure;
				}

				return groups;
			}, [])

			// todo remove? outlookIndicator no longer used	on groups
			// Determine group outlook (used for government profile view)
			indicatorGroups.forEach((indicatorGroup: IndicatorGrouping) => {
				const inputOutlooks = indicatorGroup.indicators?.map((indicator: Indicator) => {
					return indicator.outlookIndicatorForDisplayYear;
				})
				indicatorGroup.outlookIndicator = OutlookIndicator.aggregate(inputOutlooks);
			})

			// Filter out any groups that don't have measures for displayYear
			return indicatorGroups.filter((group: IndicatorGrouping) => group.indicators.find(i => i.measureForDisplayYear != null));
		}));
	}



}

